import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  Circle,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  X,
  Phone,
  MapPin,
  User,
  Truck,
  Users,
  Search,
  Hash,
  Store,
  Navigation,
  ShieldCheck,
  ShieldAlert,
  LocateFixed,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// FIX ICON
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// TASK ICON
const iconBlue = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const iconGreen = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const iconRed = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const getMarkerIcon = () => iconBlue;

const SHOP_RADIUS_METERS = 3000;

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const getDistanceInMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;

  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const formatDistance = (meters) => {
  if (!Number.isFinite(meters)) return "N/A";

  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }

  return `${(meters / 1000).toFixed(2)} KM`;
};

// RECENTER
function RecenterMap({ center, zoom }) {
  const map = useMap();

  useEffect(() => {
    map.setView(center, zoom, { animate: true });
  }, [center, zoom, map]);

  return null;
}

const RealMap = ({ Task, drivers, teams = [], showSearch = false }) => {
  const [center, setCenter] = useState([28.6139, 77.209]);
  const [zoom, setZoom] = useState(11);

  const [searchText, setSearchText] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverDetails, setShowDriverDetails] = useState(false);
  const [selectedShopForRadius, setSelectedShopForRadius] = useState(null);

  const [prevCenter, setPrevCenter] = useState(center);
  const [prevZoom, setPrevZoom] = useState(zoom);

  const validDrivers = useMemo(
    () =>
      drivers?.filter(
        (d) =>
          d.location_lat &&
          d.location_lng &&
          toNumber(d.location_lat) !== null &&
          toNumber(d.location_lng) !== null,
      ) || [],
    [drivers],
  );

  const filteredDrivers = useMemo(() => {
    if (!searchText) return [];

    return validDrivers.filter((d) =>
      `${d.fullName || ""} ${d.first_name || ""} ${d.last_name || ""} ${
        d.phone || ""
      } ${d.driverId || ""} ${d.driverCode || ""} ${d.driver_code || ""} ${
        d.teamName || ""
      }`
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    );
  }, [searchText, validDrivers]);

  const displayDrivers = useMemo(() => {
    if (selectedDriver && showDriverDetails) {
      return validDrivers.filter((driver) => {
        const selectedCode =
          selectedDriver.driverId ||
          selectedDriver.driverCode ||
          selectedDriver.driver_code ||
          selectedDriver.code;

        const driverCode =
          driver.driverId ||
          driver.driverCode ||
          driver.driver_code ||
          driver.code;

        const sameById =
          selectedDriver.id !== undefined &&
          selectedDriver.id !== null &&
          driver.id !== undefined &&
          driver.id !== null &&
          String(selectedDriver.id) === String(driver.id);

        const sameByCode =
          selectedCode &&
          driverCode &&
          String(selectedCode).trim().toLowerCase() ===
            String(driverCode).trim().toLowerCase();

        return sameById || sameByCode;
      });
    }

    return validDrivers;
  }, [selectedDriver, showDriverDetails, validDrivers]);

  const selectedDriverTeams = useMemo(() => {
    if (!selectedDriver) return [];

    return (teams || []).filter((team) => {
      const driverTeamId =
        selectedDriver.teamid !== null && selectedDriver.teamid !== undefined
          ? String(selectedDriver.teamid)
          : null;

      const teamId =
        team.id !== null && team.id !== undefined ? String(team.id) : null;

      const isSameTeamById = driverTeamId && teamId && driverTeamId === teamId;

      const isSameTeamByName =
        selectedDriver.teamName &&
        team.team_name &&
        selectedDriver.teamName.trim().toLowerCase() ===
          team.team_name.trim().toLowerCase();

      const existsInDriverList = Array.isArray(team.driverlist)
        ? team.driverlist.some((driver) => {
            const selectedDriverCode =
              selectedDriver.driverId ||
              selectedDriver.driverCode ||
              selectedDriver.driver_code ||
              selectedDriver.code;

            const selectedDriverNumericId = selectedDriver.id;

            const teamDriverCode =
              driver.driverId ||
              driver.driverCode ||
              driver.driver_code ||
              driver.driver_id ||
              driver.code;

            const teamDriverNumericId = driver.id;

            const isSameByCode =
              selectedDriverCode &&
              teamDriverCode &&
              String(selectedDriverCode).trim().toLowerCase() ===
                String(teamDriverCode).trim().toLowerCase();

            const isSameByNumericId =
              selectedDriverNumericId &&
              teamDriverNumericId &&
              String(selectedDriverNumericId) === String(teamDriverNumericId);

            return isSameByCode || isSameByNumericId;
          })
        : false;

      return isSameTeamById || isSameTeamByName || existsInDriverList;
    });
  }, [selectedDriver, teams]);

  const selectedDriverShops = useMemo(() => {
    if (!selectedDriver) return [];

    const driverLat = toNumber(selectedDriver.location_lat);
    const driverLng = toNumber(selectedDriver.location_lng);

    if (driverLat === null || driverLng === null) return [];

    return selectedDriverTeams.flatMap((team) => {
      const shops = Array.isArray(team.shopList) ? team.shopList : [];

      return shops
        .map((shop) => {
          const shopLat = toNumber(shop.shop_latitude);
          const shopLng = toNumber(shop.shop_longitude);

          if (shopLat === null || shopLng === null) return null;

          const distance = getDistanceInMeters(
            driverLat,
            driverLng,
            shopLat,
            shopLng,
          );

          return {
            ...shop,
            teamId: team.id,
            teamName: team.team_name,
            shopLat,
            shopLng,
            distance,
            isInsideRadius: distance <= SHOP_RADIUS_METERS,
          };
        })
        .filter(Boolean);
    });
  }, [selectedDriver, selectedDriverTeams]);

  useEffect(() => {
    if (!selectedDriver) {
      setSelectedShopForRadius(null);
      return;
    }

    if (selectedDriverShops.length > 0) {
      setSelectedShopForRadius(selectedDriverShops[0]);
    } else {
      setSelectedShopForRadius(null);
    }
  }, [selectedDriver, selectedDriverShops]);

  useEffect(() => {
    if (selectedDriver && showDriverDetails) {
      setCenter([
        parseFloat(selectedDriver.location_lat),
        parseFloat(selectedDriver.location_lng),
      ]);

      setZoom(13);
    }
  }, [selectedDriver, showDriverDetails]);

  const createDriverIcon = (url, isSelected) =>
    new L.Icon({
      iconUrl: url,
      iconSize: isSelected ? [40, 40] : [30, 30],
      iconAnchor: [20, 40],
    });

  return (
    <>
      {/* SEARCH BAR ONLY FULL MAP */}
      {showSearch && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[9999] w-[90%] max-w-[500px]">
          <div className="bg-white rounded-xl shadow-lg p-3">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-2.5 text-gray-400"
              />

              <input
                type="text"
                placeholder="Search driver..."
                className="w-full border rounded pl-9 pr-8 py-2 text-sm"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
              />

              {searchText && (
                <button
                  onClick={() => {
                    setSearchText("");
                    setSelectedDriver(null);
                    setShowDriverDetails(false);
                    setSelectedShopForRadius(null);
                    setCenter(prevCenter);
                    setZoom(prevZoom);
                  }}
                  className="absolute right-2 top-2 text-gray-400 hover:text-red-500"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {searchText && (
              <div className="mt-2 border rounded max-h-[200px] overflow-y-auto">
                {filteredDrivers.length > 0 ? (
                  filteredDrivers.map((d) => (
                    <div
                      key={d.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setSelectedDriver(d);
                        setShowDriverDetails(true);
                        setSelectedShopForRadius(null);
                        setSearchText("");
                      }}
                    >
                      <b>{d.fullName}</b>
                      <div className="text-xs">{d.phone}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 text-sm text-gray-500">
                    No driver found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MAP */}
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
      >
        <RecenterMap center={center} zoom={zoom} />

        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {selectedShopForRadius && (
          <>
            <Circle
              center={[
                selectedShopForRadius.shopLat,
                selectedShopForRadius.shopLng,
              ]}
              radius={SHOP_RADIUS_METERS}
              pathOptions={{
                color: selectedShopForRadius.isInsideRadius
                  ? "#16a34a"
                  : "#dc2626",
                fillColor: selectedShopForRadius.isInsideRadius
                  ? "#16a34a"
                  : "#dc2626",
                fillOpacity: 0.12,
                weight: 2,
              }}
            />

            <Marker
              position={[
                selectedShopForRadius.shopLat,
                selectedShopForRadius.shopLng,
              ]}
              icon={selectedShopForRadius.isInsideRadius ? iconGreen : iconRed}
            >
              <Popup>
                <b>{selectedShopForRadius.shop_name || "Shop"}</b>
                <br />
                Team: {selectedShopForRadius.teamName || "N/A"}
                <br />
                Radius: 3 KM
                <br />
                Driver Distance:{" "}
                {formatDistance(selectedShopForRadius.distance)}
                <br />
                Status:{" "}
                {selectedShopForRadius.isInsideRadius
                  ? "Driver is inside this shop radius"
                  : "Driver is outside this shop radius"}
              </Popup>
            </Marker>
          </>
        )}

        <MarkerClusterGroup>
          {displayDrivers.map((driver) => {
            const isSelected = selectedDriver?.id === driver.id;

            const icon = createDriverIcon(
              isSelected
                ? "https://cdn-icons-png.flaticon.com/512/684/684908.png"
                : driver.driverIcon || getMarkerIcon().options.iconUrl,
              isSelected,
            );

            return (
              <Marker
                key={driver.id}
                position={[
                  parseFloat(driver.location_lat),
                  parseFloat(driver.location_lng),
                ]}
                icon={icon}
                eventHandlers={{
                  click: () => {
                    setSelectedDriver(driver);
                    setShowDriverDetails(false);
                    setSelectedShopForRadius(null);
                  },
                }}
              >
                <Popup>
                  <b>{driver.fullName || "N/A"}</b>
                  <br />
                  Mobile: {driver.phone || "N/A"}
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>

      {/* DRIVER CARD */}

      {selectedDriver && showDriverDetails && (
        <div className="absolute bottom-5 left-5 z-[9999] w-[380px] max-w-[calc(100%-40px)]">
          <div className="bg-white rounded-2xl shadow-xl p-4 text-sm relative border max-h-[72vh] overflow-y-auto">
            {/* CLEAR BUTTON */}
            <button
              onClick={() => {
                setSelectedDriver(null);
                setShowDriverDetails(false);
                setSelectedShopForRadius(null);
                setCenter(prevCenter);
                setZoom(prevZoom);
              }}
              className="absolute top-2 right-10 text-xs text-gray-500 hover:text-red-500"
            >
              Clear
            </button>

            {/* CLOSE BUTTON */}
            <button
              onClick={() => {
                setShowDriverDetails(false);
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
            >
              <X size={18} />
            </button>

            {/* HEADER */}
            <div className="flex items-center gap-2 mb-3 pr-16">
              <User size={18} className="text-blue-500" />
              <span className="font-semibold text-gray-800 text-base">
                {selectedDriver.fullName || "N/A"}
              </span>
            </div>

            {/* DRIVER DETAILS */}
            <div className="space-y-2 text-gray-600">
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-green-500" />
                <span>{selectedDriver.phone || "N/A"}</span>
              </div>

              <div className="flex items-center gap-2">
                <Truck size={16} className="text-yellow-500" />
                <span>Status: {selectedDriver.dutyStatus || "N/A"}</span>
              </div>

              <div className="flex items-start gap-2">
                <Users size={16} className="text-purple-500 mt-0.5" />
                <span>
                  Team:{" "}
                  {selectedDriverTeams.length > 0
                    ? selectedDriverTeams
                        .map((team) => team.team_name)
                        .filter(Boolean)
                        .join(", ")
                    : selectedDriver.teamName || "N/A"}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Hash size={16} className="text-gray-500" />
                <span>
                  Code:{" "}
                  {selectedDriver.driverId ||
                    selectedDriver.driverCode ||
                    selectedDriver.driver_code ||
                    "N/A"}
                </span>
              </div>

              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-red-500 mt-0.5" />
                <span>
                  {selectedDriver.location_lat}, {selectedDriver.location_lng}
                </span>
              </div>

              {/* SHOP RADIUS CHECK */}
              <div className="mt-3 border-t pt-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <LocateFixed size={16} className="text-blue-500" />
                    <span className="font-semibold text-gray-800">
                      Shop Radius Check
                    </span>
                  </div>

                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                      selectedDriverShops.length === 0
                        ? "bg-gray-100 text-gray-600"
                        : selectedShopForRadius?.isInsideRadius
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                    }`}
                  >
                    {selectedDriverShops.length === 0 ? (
                      <>
                        <Store size={12} />
                        No shop mapped
                      </>
                    ) : selectedShopForRadius?.isInsideRadius ? (
                      <>
                        <ShieldCheck size={12} />
                        Inside 3 KM
                      </>
                    ) : (
                      <>
                        <ShieldAlert size={12} />
                        Outside 3 KM
                      </>
                    )}
                  </span>
                </div>

                {selectedDriverShops.length > 0 ? (
                  <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                    {selectedDriverShops.map((shop) => {
                      const isSelectedShop =
                        selectedShopForRadius?.id === shop.id &&
                        selectedShopForRadius?.teamId === shop.teamId;

                      return (
                        <div
                          key={`${shop.teamId}-${shop.id}`}
                          onClick={() => {
                            setSelectedShopForRadius(shop);
                          }}
                          className={`border rounded-lg p-2 cursor-pointer transition ${
                            isSelectedShop
                              ? "bg-blue-50 border-blue-400 ring-2 ring-blue-400"
                              : shop.isInsideRadius
                                ? "bg-green-50 border-green-200 hover:bg-green-100"
                                : "bg-red-50 border-red-200 hover:bg-red-100"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <Store
                              size={15}
                              className={
                                isSelectedShop
                                  ? "text-blue-600 mt-0.5"
                                  : shop.isInsideRadius
                                    ? "text-green-600 mt-0.5"
                                    : "text-red-600 mt-0.5"
                              }
                            />

                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 truncate">
                                {shop.shop_name || "Unnamed Shop"}
                              </div>

                              <div className="text-xs text-gray-500">
                                Team: {shop.teamName || "N/A"}
                              </div>

                              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                <Navigation size={12} />
                                Distance: {formatDistance(shop.distance)}
                              </div>

                              <div
                                className={`text-xs font-semibold mt-1 flex items-center gap-1 ${
                                  isSelectedShop
                                    ? "text-blue-600"
                                    : shop.isInsideRadius
                                      ? "text-green-600"
                                      : "text-red-600"
                                }`}
                              >
                                {isSelectedShop ? (
                                  <>
                                    <LocateFixed size={12} />
                                    Showing this shop radius on map
                                  </>
                                ) : shop.isInsideRadius ? (
                                  <>
                                    <ShieldCheck size={12} />
                                    Driver is inside this shop&apos;s 3 KM
                                    radius
                                  </>
                                ) : (
                                  <>
                                    <ShieldAlert size={12} />
                                    Driver is outside this shop&apos;s 3 KM
                                    radius
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 border rounded-lg p-3 bg-gray-50 flex items-start gap-2">
                    <Store size={15} className="text-gray-400 mt-0.5" />
                    <span>No shop is mapped against this driver's team.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RealMap;